import * as React from 'react';
import { cn } from '@savvy-kit/lib/utils';

// A bordered, rounded list container. `divide-y` draws a separator between rows automatically, so
// every ListItem gets one above it except the first — no manual dividers on the last row.
function List({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="list"
      role="list"
      className={cn('divide-y divide-light overflow-hidden rounded-lg border border-light', className)}
      {...props}
    />
  );
}

function ListItem({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="list-item"
      role="listitem"
      className={cn('flex items-center gap-3 px-4 py-3', className)}
      {...props}
    />
  );
}

// Grows to fill the row so ListItemAction is pushed to the far right; min-w-0 lets its text truncate.
function ListItemContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="list-item-content" className={cn('flex min-w-0 flex-1 flex-col', className)} {...props} />;
}

// `primary` is the emphasized top line, `secondary` the muted caption below it. Both are booleans; a
// row typically renders one of each.
function ListItemText({
  className,
  primary = false,
  secondary = false,
  ...props
}: React.ComponentProps<'p'> & { primary?: boolean; secondary?: boolean }) {
  return (
    <p
      data-slot="list-item-text"
      className={cn(
        'text-sm text-foreground',
        primary && 'font-medium',
        secondary && 'text-xs text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function ListItemAction({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="list-item-action" className={cn('flex shrink-0 items-center', className)} {...props} />;
}

export { List, ListItem, ListItemContent, ListItemText, ListItemAction };
